import { sortearDuplaMedicao } from '@equiview/domain';
import { PROTOCOL_V1_ID, traitCodeId, traitCodePorId } from '@equiview/protocol';
import { sha256DoBlob, uuidv7, validarArquivo } from '@equiview/sync';
import { liveQuery } from 'dexie';
import { gravarHarasAtual, lerHarasAtual } from '../lib/harasAtual.js';
import { arquivos, db, engine, sincronizarAgora } from '../lib/local.js';
import { supabase } from '../lib/supabase.js';
import type {
  AnaliseDeMidia,
  Animal,
  Avaliacao,
  Emenda,
  PessoaDaEquipe,
  FichaDeColeta,
  FonteDeDados,
  Haras,
  Manejo,
  Medida,
  MedicaoPorFoto,
  Midia,
  Observacao,
  Usuario,
  Visita,
} from './fonte.js';

/**
 * Fonte sobre o banco do aparelho (Dexie). Le o que foi cadastrado aqui ou
 * recebido pelo sync; grava sempre por `engine.registrar`, que poe a linha na
 * tela na hora e o item na fila de envio, na mesma transacao.
 *
 * Medidas: `measurement` guarda `trait_code_id`, e o catalogo de traits nunca
 * chega ao aparelho — o id sai do mapa fixo do protocolo (TRAIT_CODE_IDS).
 */

type Linha = Record<string, unknown>;
const vivas = <T extends Linha>(linhas: T[]) => linhas.filter((l) => !l.deleted_at);
const texto = (v: unknown) => (typeof v === 'string' && v.trim() ? v : null);

function paraHaras(l: Linha): Haras {
  return {
    id: String(l.id),
    name: String(l.name ?? ''),
    city: texto(l.city),
    uf: texto(l.uf),
    abccmm_code: texto(l.abccmm_code),
    owner_contact: texto(l.owner_contact),
    data_sharing_level: (l.data_sharing_level as Haras['data_sharing_level']) ?? 'privado',
    notes: texto(l.notes),
  };
}

function paraAnimal(l: Linha): Animal {
  return {
    id: String(l.id),
    abccmm_registry: texto(l.abccmm_registry),
    name: String(l.name ?? ''),
    sex: l.sex as Animal['sex'],
    birth_date: String(l.birth_date ?? ''),
    birth_date_estimated: l.birth_date_estimated === true,
    stud_book: texto(l.stud_book),
    microchip: texto(l.microchip),
    coat_color: texto(l.coat_color),
    marcha_type_declared: (l.marcha_type_declared as Animal['marcha_type_declared']) ?? null,
    owner_farm_id: texto(l.owner_farm_id),
    breeder_farm_id: texto(l.breeder_farm_id),
    sire_id: texto(l.sire_id),
    dam_id: texto(l.dam_id),
    notes: texto(l.notes),
  };
}

function paraVisita(l: Linha): Visita {
  return {
    id: String(l.id),
    stud_farm_id: String(l.stud_farm_id),
    visit_number: Number(l.visit_number),
    visit_date: String(l.visit_date),
    protocol_version_id: String(l.protocol_version_id),
    closed_at: texto(l.closed_at),
    purpose: l.purpose === 'estudo_repetibilidade' ? 'estudo_repetibilidade' : 'rotina',
    weather: (l.weather as Visita['weather']) ?? null,
    temp_c: numero(l.temp_c),
    notes: texto(l.notes),
  };
}

const numero = (v: unknown) => (v == null || v === '' ? null : Number(v));

function paraAvaliacao(l: Linha, visitDate: string): Avaliacao {
  return {
    id: String(l.id),
    animal_id: String(l.animal_id),
    visit_id: String(l.visit_id),
    visit_date: visitDate,
    protocol_level: Number(l.protocol_level) as Avaliacao['protocol_level'],
    status: (l.status as Avaliacao['status']) ?? 'rascunho',
    started_at: texto(l.started_at),
    ended_at: texto(l.ended_at),
    sampled_for_double_measure: l.sampled_for_double_measure === true,
  };
}

/** Linha de `animal_management` (ou nada, antes da Estacao 1) para a ficha. */
function paraManejo(l: Linha | undefined): Manejo {
  const m = l ?? {};
  const escolha = <T,>(v: unknown) => (v == null || v === '' ? null : (v as T));
  return {
    weight_kg: numero(m.weight_kg),
    weight_method: escolha(m.weight_method),
    bcs_henneke: numero(m.bcs_henneke),
    last_feed_time: texto(m.last_feed_time),
    water_access: escolha(m.water_access),
    arrived_farm_gt_30d: typeof m.arrived_farm_gt_30d === 'boolean' ? m.arrived_farm_gt_30d : null,
    female_status: escolha(m.female_status),
    gestation_months: numero(m.gestation_months),
    days_postpartum: numero(m.days_postpartum),
    male_status: escolha(m.male_status),
    covers_last_week: numero(m.covers_last_week),
    training_status: escolha(m.training_status),
    training_months: numero(m.training_months),
    last_shoeing_date: texto(m.last_shoeing_date),
    shod_fl: m.shod_fl === true,
    shod_fr: m.shod_fr === true,
    shod_hl: m.shod_hl === true,
    shod_hr: m.shod_hr === true,
    barefoot: m.barefoot === true,
    hoof_condition: escolha(m.hoof_condition),
    fitness: escolha(m.fitness),
    lameness_limb: escolha(m.lameness_limb),
    lameness_aaep: numero(m.lameness_aaep),
    dna_sample_collected: m.dna_sample_collected === true,
    dna_tube_id: texto(m.dna_tube_id),
  };
}


/** Ficha que ainda aceita coleta. `reaberta` e a volta de uma ficha fechada para correcao. */
const editavel = (status: unknown) => status === 'rascunho' || status === 'reaberta';

/** Linha vazia vira null: o banco nao quer string vazia onde cabe "nao informado". */
function limpar(o: Record<string, unknown>) {
  return Object.fromEntries(Object.entries(o).map(([k, v]) => [k, typeof v === 'string' && !v.trim() ? null : v]));
}

export function criarFonteLocal(usuario: () => Promise<Usuario | null>): FonteDeDados {
  const t = (nome: string) => db.table<Linha>(nome);

  const todosHaras = async () => vivas(await t('stud_farm').toArray()).map(paraHaras);
  const todosAnimais = async () => vivas(await t('animal').toArray()).map(paraAnimal);
  const visitasDe = async (harasId: string) =>
    vivas(await t('visit').where('stud_farm_id').equals(harasId).toArray()).map(paraVisita);

  async function harasAtual(): Promise<Haras | null> {
    const lista = await todosHaras();
    const id = lerHarasAtual();
    // Sem escolha (ou escolha apagada), vale o primeiro: com um haras so, nao ha o que escolher.
    return lista.find((h) => h.id === id) ?? lista.sort((a, b) => a.name.localeCompare(b.name, 'pt-BR'))[0] ?? null;
  }

  async function avaliacoes(animalId?: string): Promise<Avaliacao[]> {
    const linhas = vivas(
      animalId ? await t('assessment').where('animal_id').equals(animalId).toArray() : await t('assessment').toArray(),
    );
    const visitas = new Map(vivas(await t('visit').toArray()).map((v) => [String(v.id), paraVisita(v)]));
    return linhas
      .map((l) => paraAvaliacao(l, visitas.get(String(l.visit_id))?.visit_date ?? ''))
      .filter((a) => a.visit_date)
      .sort((x, y) => x.visit_date.localeCompare(y.visit_date));
  }

  /**
   * Sessoes da rodada normal de campo. A dupla medicao (replicate 2) e a medida
   * por foto sao outras sessoes e ficam de fora: a ficha mostra a rodada normal.
   */
  const sessoesDeCampo = async (avaliacaoIds: readonly string[], replica = 1) =>
    vivas(await t('measurement_session').where('assessment_id').anyOf([...avaliacaoIds]).toArray()).filter(
      (s) => Number(s.replicate_no ?? 1) === replica && (s.method ?? 'campo') === 'campo',
    );

  async function medidas(avaliacaoIds: readonly string[], replica = 1): Promise<Medida[]> {
    if (avaliacaoIds.length === 0) return [];
    const fichaDaSessao = new Map((await sessoesDeCampo(avaliacaoIds, replica)).map((s) => [String(s.id), String(s.assessment_id)]));
    if (fichaDaSessao.size === 0) return [];
    const linhas = vivas(await t('measurement').where('session_id').anyOf([...fichaDaSessao.keys()]).toArray());
    return linhas.flatMap((m): Medida[] => {
      const code = traitCodePorId(String(m.trait_code_id));
      // Corrigida por emenda: vale a que a substituiu.
      if (!code || m.superseded_by) return [];
      return [
        {
          id: String(m.id),
          assessment_id: fichaDaSessao.get(String(m.session_id))!,
          trait_code: code,
          side: (m.side as Medida['side']) ?? 'NA',
          value_num: numero(m.value_num),
          not_applicable: m.not_applicable === true,
        },
      ];
    });
  }

  /** Todas as sessoes de campo da ficha, cada uma com as proprias medidas. */
  async function sessoesComMedidas(avaliacaoId: string) {
    const sessoes = vivas(await t('measurement_session').where('assessment_id').equals(avaliacaoId).toArray()).filter(
      (s) => (s.method ?? 'campo') === 'campo',
    );
    const linhas = vivas(await t('measurement').where('session_id').anyOf(sessoes.map((s) => String(s.id))).toArray());
    return sessoes
      .map((s) => ({
        id: String(s.id),
        evaluator_id: String(s.evaluator_id),
        recorder_id: texto(s.recorder_id),
        replicate_no: Number(s.replicate_no ?? 1),
        medidas: linhas.flatMap((m): Medida[] => {
          const code = traitCodePorId(String(m.trait_code_id));
          if (m.session_id !== s.id || !code || m.superseded_by) return [];
          return [
            {
              id: String(m.id),
              assessment_id: avaliacaoId,
              trait_code: code,
              side: (m.side as Medida['side']) ?? 'NA',
              value_num: numero(m.value_num),
              not_applicable: m.not_applicable === true,
            },
          ];
        }),
      }))
      .sort((x, y) => x.replicate_no - y.replicate_no || x.evaluator_id.localeCompare(y.evaluator_id));
  }

  const paraSessao = (s: Linha | undefined) =>
    s ? { id: String(s.id), evaluator_id: String(s.evaluator_id), recorder_id: texto(s.recorder_id) } : null;

  /**
   * Marca o inicio da coleta da ficha. Na primeira ficha iniciada da visita, faz
   * o sorteio da dupla medicao — antes de qualquer medida, para ninguem escolher.
   * Dois tablets offline podem sortear cada um o seu: a amostra so cresce, e
   * continua >= 10%.
   */
  async function iniciarFicha(a: Linha): Promise<void> {
    if (a.started_at) return;
    await engine.registrar('assessment', String(a.id), { started_at: new Date().toISOString() });
    // No estudo da Fase 0 toda ficha ja e multi-sessao: nao ha sorteio.
    if ((await t('visit').get(String(a.visit_id)))?.purpose === 'estudo_repetibilidade') return;
    const daVisita = vivas(await t('assessment').where('visit_id').equals(String(a.visit_id)).toArray());
    if (daVisita.some((x) => x.sampled_for_double_measure === true)) return;
    for (const sorteada of sortearDuplaMedicao(daVisita)) {
      await engine.registrar('assessment', String(sorteada.id), { sampled_for_double_measure: true });
    }
  }

  /** A ficha tem de existir e ainda aceitar coleta; o servidor recusaria de qualquer jeito. */
  async function fichaEditavel(avaliacaoId: string): Promise<Linha> {
    const a = await t('assessment').get(avaliacaoId);
    if (!a || a.deleted_at) throw new Error('Ficha não encontrada.');
    if (!editavel(a.status)) throw new Error('Esta ficha já foi fechada. Correção agora só por emenda.');
    return a;
  }

  /**
   * Linhas de `media` como a tela as ve: o andamento vem de `gait_trial`
   * (video_id), e `pendente` diz se o arquivo esta neste aparelho esperando rede.
   */
  async function paraMidias(linhas: Linha[]): Promise<Midia[]> {
    if (linhas.length === 0) return [];
    const fichas = [...new Set(linhas.map((l) => String(l.assessment_id)))];
    const andamentos = new Map(
      vivas(await t('gait_trial').where('assessment_id').anyOf(fichas).toArray())
        .filter((g) => g.video_id)
        .map((g) => [String(g.video_id), g.gait as Midia['gait']]),
    );
    const noAparelho = new Set((await arquivos.pendentes()).map((a) => a.mediaId));
    return vivas(linhas).map((l) => ({
      id: String(l.id),
      assessment_id: String(l.assessment_id),
      kind: l.kind as Midia['kind'],
      view: (l.view as Midia['view']) ?? null,
      gait: andamentos.get(String(l.id)) ?? null,
      storage_path: texto(l.storage_path),
      pendente: noAparelho.has(String(l.id)),
    }));
  }

  /** Links assinados do Storage, reaproveitados enquanto valem (1 h). */
  const links = new Map<string, { url: string; expira: number }>();

  async function guardarMidia(avaliacaoId: string, arquivo: File, dados: Record<string, unknown>): Promise<string> {
    const a = await t('assessment').get(avaliacaoId);
    if (!a || a.deleted_at) throw new Error('Ficha não encontrada.');
    const id = uuidv7();
    await engine.registrar('media', id, {
      assessment_id: avaliacaoId,
      filename: arquivo.name,
      sha256: await sha256DoBlob(arquivo),
      captured_at: new Date(arquivo.lastModified || Date.now()).toISOString(),
      ...dados,
    });
    await arquivos.guardar({
      mediaId: id,
      assessmentId: avaliacaoId,
      blob: arquivo,
      mime: arquivo.type,
      tamanho: arquivo.size,
      estado: 'pendente',
      erro: null,
      criadoEm: Date.now(),
    });
    // Com rede, ja sobe; sem rede, a proxima sincronizacao leva.
    if (navigator.onLine) void sincronizarAgora().catch(() => undefined);
    return id;
  }

  /** Valor de banco como texto, para `amendment.old_value/new_value`. */
  const comoTexto = (v: unknown) => (v == null || v === '' ? null : String(v));

  async function emendar(entity: string, entityId: string, field: string, novo: unknown, motivo: string) {
    const u = await usuario();
    if (!u) throw new Error('Entre com seu usuário para registrar a emenda.');
    if (motivo.trim().length < 10) throw new Error('O motivo da emenda precisa de pelo menos 10 caracteres.');
    const atual = await t(entity).get(entityId);
    if (!atual) throw new Error('Registro não encontrado neste aparelho.');
    if (comoTexto(atual[field]) === comoTexto(novo)) return;
    await engine.emendar(
      { entity, entityId, field, valorAntigo: comoTexto(atual[field]), valorNovo: comoTexto(novo), motivo, autor: u.id },
      { [field]: novo },
    );
  }

  /** Reabrir e validar: decisao de coordenacao. O servidor confere de novo. */
  async function mudarStatus(avaliacaoId: string, de: readonly string[], para: 'reaberta' | 'validada', motivo: string) {
    const u = await usuario();
    if (u?.papel !== 'admin') throw new Error('Só o admin reabre ou valida uma ficha.');
    const a = await t('assessment').get(avaliacaoId);
    if (!a || !de.includes(String(a.status))) throw new Error(`A ficha não pode passar de ${String(a?.status)} para ${para}.`);
    await emendar('assessment', avaliacaoId, 'status', para, motivo);
  }

  const registrarAvaliacao = (visita: Visita, animalId: string, nivel: 1 | 2) =>
    engine.registrar('assessment', uuidv7(), {
      visit_id: visita.id,
      animal_id: animalId,
      protocol_level: nivel,
      protocol_version_id: visita.protocol_version_id,
      status: 'rascunho',
    });

  return {
    ficticia: false,

    observar: <T>(consulta: () => Promise<T>) => liveQuery(consulta),

    haras: harasAtual,
    listarHaras: todosHaras,
    listarAnimais: todosAnimais,
    animal: async (id) => {
      const l = await t('animal').get(id);
      return l && !l.deleted_at ? paraAnimal(l) : null;
    },
    filhos: async (id) => (await todosAnimais()).filter((a) => a.sire_id === id || a.dam_id === id),
    avaliacoes,
    medidas,
    observacoes: async (avaliacaoIds): Promise<Observacao[]> => {
      if (avaliacaoIds.length === 0) return [];
      const sessoes = new Map(
        vivas(await t('measurement_session').where('assessment_id').anyOf([...avaliacaoIds]).toArray()).map((s) => [String(s.id), s]),
      );
      if (sessoes.size === 0) return [];
      return vivas(await t('measurement').where('session_id').anyOf([...sessoes.keys()]).toArray()).flatMap((m): Observacao[] => {
        const s = sessoes.get(String(m.session_id));
        const code = traitCodePorId(String(m.trait_code_id));
        if (!s || !code || m.superseded_by) return [];
        return [
          {
            assessment_id: String(s.assessment_id),
            avaliador: String(s.evaluator_id),
            rodada: Number(s.replicate_no ?? 1),
            metodo: (s.method as Observacao['metodo']) ?? 'campo',
            trait_code: code,
            side: (m.side as Medida['side']) ?? 'NA',
            value_num: numero(m.value_num),
            not_applicable: m.not_applicable === true,
          },
        ];
      });
    },
    manejos: async (avaliacaoIds) => {
      const linhas = await t('animal_management').bulkGet([...avaliacaoIds]);
      return Object.fromEntries(linhas.flatMap((l, i) => (l ? [[avaliacaoIds[i]!, paraManejo(l)]] : [])));
    },
    listarVisitas: async () => {
      const h = await harasAtual();
      return h ? (await visitasDe(h.id)).sort((a, b) => b.visit_number - a.visit_number) : [];
    },
    midias: async (avaliacaoId) => paraMidias(await t('media').where('assessment_id').equals(avaliacaoId).toArray()),
    midia: async (id) => {
      const l = await t('media').get(id);
      return l ? ((await paraMidias([l]))[0] ?? null) : null;
    },
    urlDaMidia: async (m) => {
      const local = await arquivos.obter(m.id);
      if (local) return URL.createObjectURL(local.blob);
      if (!m.storage_path || !navigator.onLine) return null;
      const guardado = links.get(m.storage_path);
      if (guardado && guardado.expira > Date.now()) return guardado.url;
      const { data, error } = await supabase.storage.from('midia').createSignedUrl(m.storage_path, 3600);
      if (error || !data) return null;
      // Margem de 5 min: o link nunca e entregue a beira de vencer.
      links.set(m.storage_path, { url: data.signedUrl, expira: Date.now() + 55 * 60_000 });
      return data.signedUrl;
    },
    capas: async () => {
      const fotos = vivas(await t('media').toArray()).filter((m) => m.kind === 'foto' && m.view === 'perfil_esquerdo');
      if (fotos.length === 0) return new Map();
      const animalDaFicha = new Map(vivas(await t('assessment').toArray()).map((a) => [String(a.id), String(a.animal_id)]));
      const recentes = new Map<string, Linha>();
      for (const f of fotos) {
        const animal = animalDaFicha.get(String(f.assessment_id));
        if (!animal) continue;
        const atual = recentes.get(animal);
        if (!atual || String(f.captured_at ?? f.created_at) > String(atual.captured_at ?? atual.created_at)) recentes.set(animal, f);
      }
      const midias = await paraMidias([...recentes.values()]);
      return new Map([...recentes.keys()].map((animal, i) => [animal, midias[i]!]));
    },
    analises: async (midiaId) => {
      const pessoas = new Map((await t('person').toArray()).map((p) => [String(p.id), String(p.full_name ?? '')]));
      return (await t('media_analysis').where('media_id').equals(midiaId).toArray())
        .map(
          (l): AnaliseDeMidia => ({
            id: String(l.id),
            media_id: String(l.media_id),
            author_id: String(l.author_id),
            author_name: pessoas.get(String(l.author_id)) || 'Equipe',
            body: String(l.body ?? ''),
            supersedes: texto(l.supersedes),
            created_at: String(l.created_at ?? ''),
            deleted_at: texto(l.deleted_at),
          }),
        )
        .sort((x, y) => y.created_at.localeCompare(x.created_at));
    },
    salvarAnalise: async ({ media_id, body, supersedes }) => {
      const u = await usuario();
      if (!u) throw new Error('Entre com seu usuário para salvar a análise.');
      const id = uuidv7();
      await engine.registrar('media_analysis', id, { media_id, author_id: u.id, body: body.trim(), supersedes });
      const l = (await t('media_analysis').get(id))!;
      return {
        id,
        media_id,
        author_id: u.id,
        author_name: u.nome,
        body: body.trim(),
        supersedes,
        created_at: String(l.created_at),
        deleted_at: null,
      };
    },
    usuarioAtual: usuario,

    cadastro: {
      salvarHaras: async ({ id, ...dados }) => {
        const novoId = id ?? uuidv7();
        await engine.registrar('stud_farm', novoId, limpar(dados));
        if (!id) gravarHarasAtual(novoId);
        return novoId;
      },
      salvarAnimal: async ({ id, ...dados }) => {
        const novoId = id ?? uuidv7();
        await engine.registrar('animal', novoId, limpar(dados));
        return novoId;
      },
      arquivarAnimal: async (id) => {
        await engine.registrar('animal', id, {}, 'delete');
      },
      escolherHaras: gravarHarasAtual,
      visitas: async () => {
        const h = await harasAtual();
        return h ? (await visitasDe(h.id)).sort((a, b) => b.visit_number - a.visit_number) : [];
      },
      visita: async (id) => {
        const l = await t('visit').get(id);
        return l && !l.deleted_at ? paraVisita(l) : null;
      },
      planejarVisita: async ({ visit_date, visit_number, purpose, itens }) => {
        const h = await harasAtual();
        if (!h) throw new Error('Escolha um haras antes de planejar a visita.');
        const visita: Visita = {
          id: uuidv7(),
          stud_farm_id: h.id,
          visit_number,
          visit_date,
          protocol_version_id: PROTOCOL_V1_ID,
          closed_at: null,
          purpose: purpose ?? 'rotina',
          weather: null,
          temp_c: null,
          notes: null,
        };
        // Visita antes das avaliacoes: a fila sai nessa ordem, e as chaves estrangeiras dependem dela.
        await engine.registrar('visit', visita.id, { ...visita });
        for (const i of itens) await registrarAvaliacao(visita, i.animal_id, i.protocol_level);
        return visita.id;
      },
      incluirNaVisita: async (visitId, animalId, nivel) => {
        const l = await t('visit').get(visitId);
        if (!l) throw new Error('Visita não encontrada.');
        const existente = (await t('assessment').where('visit_id').equals(visitId).toArray()).find(
          (a) => a.animal_id === animalId,
        );
        if (existente && !existente.deleted_at) return;
        // unique (visit_id, animal_id): quem foi retirado volta pela mesma linha, e nao por uma nova.
        if (existente) {
          await engine.registrar('assessment', String(existente.id), { deleted_at: null, protocol_level: nivel });
          return;
        }
        await registrarAvaliacao(paraVisita(l), animalId, nivel);
      },
      retirarDaVisita: async (avaliacaoId) => {
        const a = await t('assessment').get(avaliacaoId);
        if (a?.status !== 'rascunho' || a.started_at) throw new Error('Só dá para retirar um animal que ainda não foi avaliado.');
        await engine.registrar('assessment', avaliacaoId, {}, 'delete');
      },

      ficha: async (avaliacaoId): Promise<FichaDeColeta | null> => {
        const a = await t('assessment').get(avaliacaoId);
        if (!a || a.deleted_at) return null;
        const [v, animal, sessao, manejo] = await Promise.all([
          t('visit').get(String(a.visit_id)),
          t('animal').get(String(a.animal_id)),
          sessoesDeCampo([avaliacaoId]).then((s) => s[0]),
          t('animal_management').get(avaliacaoId),
        ]);
        if (!v || !animal) return null;
        return {
          avaliacao: paraAvaliacao(a, String(v.visit_date)),
          visita: paraVisita(v),
          animal: paraAnimal(animal),
          sessao: paraSessao(sessao),
          medidas: await medidas([avaliacaoId]),
          sessao2: paraSessao((await sessoesDeCampo([avaliacaoId], 2))[0]),
          medidas2: await medidas([avaliacaoId], 2),
          sessoes: await sessoesComMedidas(avaliacaoId),
          marcadores: Object.fromEntries(
            vivas(await t('marker_check').where('assessment_id').equals(avaliacaoId).toArray()).map((m) => [
              String(m.marker_code),
              m.placed === true,
            ]),
          ),
          midias: await paraMidias(await t('media').where('assessment_id').equals(avaliacaoId).toArray()),
          manejo: paraManejo(manejo),
        };
      },

      pessoasDaEquipe: async () =>
        vivas(await t('person').toArray())
          .filter((p) => p.active !== false && (p.app_role === 'admin' || p.app_role === 'avaliador'))
          .map((p) => ({ id: String(p.id), nome: String(p.full_name ?? '') }))
          .sort((x, y) => x.nome.localeCompare(y.nome, 'pt-BR')),

      abrirSessao: async (avaliacaoId, medidorId, replica = 1) => {
        const u = await usuario();
        if (!u) throw new Error('Entre com seu usuário para registrar a coleta.');
        // Folha 8 da ficha: quem mede nunca e quem registra. O banco tambem recusa.
        if (u.id === medidorId) throw new Error('Quem mede não pode ser quem registra. Escolha outro medidor.');
        const a = await fichaEditavel(avaliacaoId);
        const estudo = (await t('visit').get(String(a.visit_id)))?.purpose === 'estudo_repetibilidade';
        if (replica === 2 && !estudo) {
          const primeira = (await sessoesDeCampo([avaliacaoId], 1))[0];
          if (!primeira) throw new Error('A segunda medição começa depois da primeira.');
          // PRD-006: a dupla medicao so vale com outro avaliador.
          if (String(primeira.evaluator_id) === medidorId) throw new Error('A segunda medição é de outro avaliador.');
        }
        const agora = new Date().toISOString();
        await iniciarFicha(a);
        // Rotina: uma sessao por rodada (trocar o medidor e editar a mesma).
        // Estudo: uma por (avaliador, rodada), o mesmo unique do banco.
        const existente = (await sessoesDeCampo([avaliacaoId], replica)).find((s) => !estudo || String(s.evaluator_id) === medidorId);
        const id = existente ? String(existente.id) : uuidv7();
        await engine.registrar('measurement_session', id, {
          assessment_id: avaliacaoId,
          evaluator_id: medidorId,
          recorder_id: u.id,
          replicate_no: replica,
          method: 'campo',
          ...(existente ? {} : { started_at: agora }),
        });
        return id;
      },

      salvarMedida: async (sessaoId, traitCode, side, valor, naoSeAplica = false) => {
        const sessao = await t('measurement_session').get(sessaoId);
        if (!sessao) throw new Error('Escolha quem está medindo antes de registrar as medidas.');
        await fichaEditavel(String(sessao.assessment_id));
        const traitId = traitCodeId(traitCode);
        // unique (session_id, trait_code_id, side): corrigir e regravar a mesma linha.
        const existente = (await t('measurement').where('session_id').equals(sessaoId).toArray()).find(
          (m) => m.trait_code_id === traitId && (m.side ?? 'NA') === side,
        );
        if (valor == null && !naoSeAplica) {
          if (existente && !existente.deleted_at) await engine.registrar('measurement', String(existente.id), {}, 'delete');
          return;
        }
        await engine.registrar('measurement', existente ? String(existente.id) : uuidv7(), {
          session_id: sessaoId,
          trait_code_id: traitId,
          side,
          value_num: naoSeAplica ? null : valor,
          not_applicable: naoSeAplica,
          observed_at: new Date().toISOString(),
          deleted_at: null,
        });
      },

      salvarManejo: async (avaliacaoId, manejo) => {
        const a = await fichaEditavel(avaliacaoId);
        // A Estacao 1 vem antes das medidas: e ela que marca o inicio da coleta.
        await iniciarFicha(a);
        await engine.registrar('animal_management', avaliacaoId, { ...manejo });
      },

      adicionarFoto: async (avaliacaoId, vista, arquivo) => {
        const erro = validarArquivo(arquivo, 'foto');
        if (erro) throw new Error(erro);
        return guardarMidia(avaliacaoId, arquivo, { kind: 'foto', view: vista });
      },

      adicionarVideo: async (avaliacaoId, vista, andamento, arquivo) => {
        const erro = validarArquivo(arquivo, 'video');
        if (erro) throw new Error(erro);
        const id = await guardarMidia(avaliacaoId, arquivo, { kind: 'video', view: vista });
        // Frente e o animal vindo (ida); tras, indo (volta). unique (ficha,
        // andamento, passagem, vista): um video novo substitui o anterior na mesma passagem.
        const pass = vista === 'frente' ? 'ida' : 'volta';
        const existente = vivas(await t('gait_trial').where('assessment_id').equals(avaliacaoId).toArray()).find(
          (g) => g.gait === andamento && g.pass === pass && g.view === vista && !g.is_reference,
        );
        await engine.registrar('gait_trial', existente ? String(existente.id) : uuidv7(), {
          assessment_id: avaliacaoId,
          gait: andamento,
          pass,
          view: vista,
          video_id: id,
        });
        return id;
      },

      removerMidia: async (id) => {
        await engine.registrar('media', id, {}, 'delete');
        await arquivos.apagar(id);
      },

      equipe: async () => {
        const acessos = vivas(await t('farm_access').toArray());
        return vivas(await t('person').toArray())
          .map(
            (p): PessoaDaEquipe => ({
              id: String(p.id),
              nome: String(p.full_name ?? ''),
              email: texto(p.email),
              papel: (p.app_role as PessoaDaEquipe['papel']) ?? 'leitor',
              ativo: p.active !== false,
              vinculada: !!p.auth_uid,
              harasIds: acessos.filter((a) => a.person_id === p.id).map((a) => String(a.stud_farm_id)),
            }),
          )
          .sort((x, y) => x.nome.localeCompare(y.nome, 'pt-BR'));
      },

      salvarPessoa: async ({ id, ...dados }) => {
        const u = await usuario();
        if (u?.papel !== 'admin') throw new Error('Só o admin gerencia a equipe.');
        const email = dados.email.trim().toLowerCase();
        if (!dados.full_name.trim()) throw new Error('Informe o nome.');
        if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email)) throw new Error('Informe um e-mail válido: é por ele que a pessoa entra.');
        const repetido = vivas(await t('person').toArray()).find((p) => p.id !== id && String(p.email ?? '').toLowerCase() === email);
        if (repetido) throw new Error(`Já existe uma pessoa com este e-mail (${String(repetido.full_name)}).`);
        // Protecao contra se trancar para fora; o servidor confere de novo.
        if (id && id === u.id && (dados.app_role !== 'admin' || !dados.active)) {
          throw new Error('Você não pode tirar de si mesmo o papel de admin. Peça a outro admin.');
        }
        const novoId = id ?? uuidv7();
        await engine.registrar('person', novoId, { full_name: dados.full_name.trim(), email, app_role: dados.app_role, active: dados.active });
        return novoId;
      },

      definirAcessos: async (personId, harasIds) => {
        const u = await usuario();
        if (u?.papel !== 'admin') throw new Error('Só o admin gerencia a equipe.');
        const atuais = (await t('farm_access').where('person_id').equals(personId).toArray());
        const querido = new Set(harasIds);
        for (const harasId of querido) {
          const existente = atuais.find((a) => a.stud_farm_id === harasId);
          if (existente && !existente.deleted_at) continue;
          // unique (pessoa, haras): acesso revogado volta pela mesma linha.
          if (existente) await engine.registrar('farm_access', String(existente.id), { deleted_at: null });
          else await engine.registrar('farm_access', uuidv7(), { person_id: personId, stud_farm_id: harasId });
        }
        for (const a of atuais) {
          if (!a.deleted_at && !querido.has(String(a.stud_farm_id))) await engine.registrar('farm_access', String(a.id), {}, 'delete');
        }
      },

      emendarMedida: async (medidaId, { valor, naoSeAplica }, motivo) => {
        const m = await t('measurement').get(medidaId);
        if (!m) throw new Error('Medida não encontrada neste aparelho.');
        // "Nao se aplica" e valor sao duas colunas: cada mudanca e uma emenda, com o mesmo motivo.
        if ((m.not_applicable === true) !== naoSeAplica) await emendar('measurement', medidaId, 'not_applicable', naoSeAplica, motivo);
        await emendar('measurement', medidaId, 'value_num', naoSeAplica ? null : valor, motivo);
      },

      emendarManejo: async (avaliacaoId, campo, novo, motivo) => {
        await emendar('animal_management', avaliacaoId, campo, novo, motivo);
      },

      reabrirFicha: async (avaliacaoId, motivo) => mudarStatus(avaliacaoId, ['fechada', 'validada'], 'reaberta', motivo),

      validarFicha: async (avaliacaoId, observacao) => mudarStatus(avaliacaoId, ['fechada'], 'validada', observacao),

      emendas: async (avaliacaoId) => {
        const sessoes = vivas(await t('measurement_session').where('assessment_id').equals(avaliacaoId).toArray()).map((s) =>
          String(s.id),
        );
        const medidasIds = (await t('measurement').where('session_id').anyOf(sessoes).toArray()).map((m) => String(m.id));
        const alvos = [avaliacaoId, ...medidasIds];
        const pessoas = new Map((await t('person').toArray()).map((p) => [String(p.id), String(p.full_name ?? '')]));
        return (await t('amendment').where('entity_id').anyOf(alvos).toArray())
          .map(
            (l): Emenda => ({
              id: String(l.id),
              entity: String(l.entity),
              entity_id: String(l.entity_id),
              field: String(l.field),
              old_value: texto(l.old_value),
              new_value: texto(l.new_value),
              reason: String(l.reason ?? ''),
              at: String(l.at ?? l.created_at ?? ''),
              autor: pessoas.get(String(l.actor_id)) || 'Equipe',
            }),
          )
          .sort((x, y) => y.at.localeCompare(x.at));
      },

      salvarVisita: async (visitaId, dados) => {
        const v = await t('visit').get(visitaId);
        if (!v || v.deleted_at) throw new Error('Visita não encontrada.');
        if (v.closed_at) throw new Error('Visita encerrada não muda mais.');
        if (dados.visit_number != null) {
          const repetida = vivas(await t('visit').where('stud_farm_id').equals(String(v.stud_farm_id)).toArray()).find(
            (x) => x.id !== visitaId && Number(x.visit_number) === dados.visit_number,
          );
          if (repetida) throw new Error(`Já existe a visita nº ${dados.visit_number} neste haras.`);
        }
        await engine.registrar('visit', visitaId, limpar(dados as Record<string, unknown>));
      },

      marcarMarcador: async (avaliacaoId, marcador, colado) => {
        await fichaEditavel(avaliacaoId);
        // unique (ficha, marcador): mudar e regravar a mesma linha.
        const existente = (await t('marker_check').where('assessment_id').equals(avaliacaoId).toArray()).find(
          (m) => m.marker_code === marcador,
        );
        await engine.registrar('marker_check', existente ? String(existente.id) : uuidv7(), {
          assessment_id: avaliacaoId,
          marker_code: marcador,
          placed: colado,
        });
      },

      fecharVisita: async (visitaId) => {
        const fichas = vivas(await t('assessment').where('visit_id').equals(visitaId).toArray());
        const abertas = fichas.filter((f) => f.status !== 'fechada' && f.status !== 'validada');
        if (abertas.length > 0) {
          throw new Error(`Ainda há ${abertas.length} ficha(s) aberta(s). Feche ou retire da visita antes de encerrar o dia.`);
        }
        await engine.registrar('visit', visitaId, { closed_at: new Date().toISOString() });
      },

      medicaoPorFoto: async (avaliacaoId): Promise<MedicaoPorFoto> => {
        const sessoes = vivas(await t('measurement_session').where('assessment_id').equals(avaliacaoId).toArray())
          .filter((s) => s.method === 'foto')
          .sort((x, y) => String(y.updated_at ?? '').localeCompare(String(x.updated_at ?? '')));
        const sessao = sessoes[0];
        if (!sessao) return { midiaId: null, pontos: {}, angulos: {}, autor: null };
        const linhas = vivas(await t('measurement').where('session_id').equals(String(sessao.id)).toArray());
        const angulos: Record<string, number> = {};
        let nota: { midia_id?: string; pontos?: MedicaoPorFoto['pontos'] } = {};
        for (const m of linhas) {
          const code = traitCodePorId(String(m.trait_code_id));
          if (code && m.value_num != null) angulos[code] = Number(m.value_num);
          if (!nota.pontos && typeof m.note === 'string') {
            try {
              nota = JSON.parse(m.note);
            } catch {
              // Nota escrita a mao: fica so o angulo.
            }
          }
        }
        const autor = await t('person').get(String(sessao.evaluator_id));
        return { midiaId: nota.midia_id ?? null, pontos: nota.pontos ?? {}, angulos, autor: autor ? String(autor.full_name) : null };
      },

      salvarMedicaoPorFoto: async (avaliacaoId, midiaId, pontos, angulos) => {
        const u = await usuario();
        if (!u || (u.papel !== 'admin' && u.papel !== 'avaliador')) throw new Error('Só a equipe mede ângulos na foto.');
        // Uma sessao de foto por avaliador (unique ficha, avaliador, rodada, metodo).
        const existente = vivas(await t('measurement_session').where('assessment_id').equals(avaliacaoId).toArray()).find(
          (s) => s.method === 'foto' && s.evaluator_id === u.id && Number(s.replicate_no ?? 1) === 1,
        );
        const sessaoId = existente ? String(existente.id) : uuidv7();
        if (!existente) {
          await engine.registrar('measurement_session', sessaoId, {
            assessment_id: avaliacaoId,
            evaluator_id: u.id,
            recorder_id: null,
            replicate_no: 1,
            method: 'foto',
            started_at: new Date().toISOString(),
          });
        }
        // Os pontos vao junto de cada angulo: da para reabrir, conferir e remarcar.
        const nota = JSON.stringify({ midia_id: midiaId, pontos });
        const atuais = await t('measurement').where('session_id').equals(sessaoId).toArray();
        for (const [code, valor] of Object.entries(angulos)) {
          const traitId = traitCodeId(code);
          const linha = atuais.find((m) => m.trait_code_id === traitId && (m.side ?? 'NA') === 'NA');
          if (valor == null) {
            if (linha && !linha.deleted_at) await engine.registrar('measurement', String(linha.id), {}, 'delete');
            continue;
          }
          await engine.registrar('measurement', linha ? String(linha.id) : uuidv7(), {
            session_id: sessaoId,
            trait_code_id: traitId,
            side: 'NA',
            value_num: valor,
            not_applicable: false,
            note: nota,
            observed_at: new Date().toISOString(),
            deleted_at: null,
          });
        }
      },

      mudarNivel: async (avaliacaoId, nivel) => {
        await fichaEditavel(avaliacaoId);
        await engine.registrar('assessment', avaliacaoId, { protocol_level: nivel });
      },

      fecharFicha: async (avaliacaoId, justificativas) => {
        const u = await usuario();
        if (!u) throw new Error('Entre com seu usuário para fechar a ficha.');
        await fichaEditavel(avaliacaoId);
        // Justificativas antes do fechamento: a fila sai nessa ordem, e o servidor
        // trava a ficha assim que ela chega fechada.
        for (const [regra, motivo] of Object.entries(justificativas)) {
          await engine.registrar('validation_override', uuidv7(), {
            assessment_id: avaliacaoId,
            rule_id: regra,
            reason: motivo.trim(),
            actor_id: u.id,
          });
        }
        await engine.registrar('assessment', avaliacaoId, {
          status: 'fechada',
          closed_by: u.id,
          ended_at: new Date().toISOString(),
        });
      },
    },
  };
}
